/* eslint-disable react/prop-types */
import { Formik, Form } from "formik";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useEffect, useState, useCallback } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import toast from "react-hot-toast";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import InventorySummaryTable from "./info";
import { FixedAssetsInventorySummaryService } from "services/fixed-assets-inventory-summary";
import { getPrintTemplate } from "./components/printTemplate";

const initialValues = {
  unit: "",
  department: "",
  documentNumber: "",
  invHour: "",
  invDay: "",
  invMonth: "",
  invYear: "",
  committee: [
    { name: "", position: "", representative: "", role: "Chủ tịch Hội đồng" },
    { name: "", position: "", representative: "", role: "Ủy viên" },
    { name: "", position: "", representative: "", role: "Ủy viên" },
  ],
  signingDate: "",
  directorName: "",
  accountantName: "",
  headOfCommitteeName: "",
  items: [],
  total: null,
};

function FixedAssetsInventorySummaryDetail() {
  const { id } = useParams();
  const location = useLocation();
  const containEdit = location.pathname?.includes("/edit");
  const containView = location.pathname?.includes("/view");
  const navigation = useNavigate();
  const [getData, setGetData] = useState({});
  const [data, setData] = useState({});

  const [formInitials, setFormInitials] = useState(initialValues);

  useEffect(() => {
    if (!id) return;
    (async () => {
      try {
        const response = await FixedAssetsInventorySummaryService.getById(id);
        const apiData = response.data ? response.data : response;

        // Map API data to flat form (nested DTO → flat)
        const mappedItems = (apiData.items || []).map((item) => ({
          orderNumber: item.orderNumber || "",
          itemName: item.assetName || "",
          unit: item.unitOfMeasurement || "",
          code: item.assetCode || "",
          location: item.locationOfUse || "",
          bookQuantity: item.accountingBook?.quantity || "",
          bookCost: item.accountingBook?.originalCost || "",
          bookRemainingValue: item.accountingBook?.remainingValue || "",
          inventoryQuantity: item.actualInventory?.quantity || "",
          inventoryCost: item.actualInventory?.originalCost || "",
          inventoryRemainingValue: item.actualInventory?.remainingValue || "",
          diffQuantity: item.variance?.quantity || "",
          diffCost: item.variance?.originalCost || "",
          diffRemainingValue: item.variance?.remainingValue || "",
          note: item.notes || "",
        }));

        const mappedTotal = {
          bookCost: apiData.totals?.accountingBookTotal?.originalCost || "",
          bookRemainingValue: apiData.totals?.accountingBookTotal?.remainingValue || "",
          inventoryCost: apiData.totals?.actualInventoryTotal?.originalCost || "",
          inventoryRemainingValue: apiData.totals?.actualInventoryTotal?.remainingValue || "",
          diffCost: apiData.totals?.varianceTotal?.originalCost || "",
          diffRemainingValue: apiData.totals?.varianceTotal?.remainingValue || "",
        };

        // Enhanced mapping for legacy UI fields
        const loadedData = {
          ...initialValues,
          unit: apiData.company?.name || apiData.unit || "",
          department: apiData.department || "",
          documentNumber: apiData.inventoryCode || "",
          invHour: apiData.invHour || "",
          // Parse inventoryDate to day/month/year
          invDay: apiData.inventoryDate ? new Date(apiData.inventoryDate).getDate().toString() : "",
          invMonth: apiData.inventoryDate
            ? (new Date(apiData.inventoryDate).getMonth() + 1).toString()
            : "",
          invYear: apiData.inventoryDate
            ? new Date(apiData.inventoryDate).getFullYear().toString()
            : "",
          committee: apiData.inventoryCommittee || initialValues.committee,
          signingDate: apiData.signingDate || new Date().toISOString().split("T")[0],
          directorName: apiData.directorName || "Giám đốc",
          accountantName: apiData.accountantName || "Kế toán trưởng",
          headOfCommitteeName:
            apiData.headOfCommitteeName || apiData.inventoryCommittee?.[0]?.name || "Trưởng ban",
          items: mappedItems.length > 0 ? mappedItems : initialValues.items,
          total: mappedTotal,
          notes: apiData.notes || "",
        };

        setFormInitials(loadedData);
        setGetData(apiData);
      } catch (error) {
        console.error("Lỗi getById", error);
        toast.error("Không thể tải dữ liệu");
      }
    })();
  }, [id]);

  const onChangeValue = useCallback((type, value) => {
    setData((prev) => ({ ...prev, [type]: value }));
  }, []);

  const handleBack = () => navigation(-1);

  const submitForm = async (values) => {
    try {
      const num = (val) => (val && !isNaN(val) ? Number(val) : 0);

      // Prefer data state over values (flat UI fields)
      const unit = data.unit !== undefined ? data.unit : values.unit;
      const department = data.department !== undefined ? data.department : values.department;
      const inventoryCode =
        data.documentNumber !== undefined ? data.documentNumber : values.documentNumber;
      const invHour = data.invHour !== undefined ? data.invHour : values.invHour;
      const invDay = data.invDay !== undefined ? data.invDay : values.invDay;
      const invMonth = data.invMonth !== undefined ? data.invMonth : values.invMonth;
      const invYear = data.invYear !== undefined ? data.invYear : values.invYear;
      const inventoryCommittee =
        data.committee !== undefined ? data.committee : values.committee || [];
      const signingDate = data.signingDate !== undefined ? data.signingDate : values.signingDate;
      const directorName =
        data.directorName !== undefined ? data.directorName : values.directorName;
      const accountantName =
        data.accountantName !== undefined ? data.accountantName : values.accountantName;
      const headOfCommitteeName =
        data.headOfCommitteeName !== undefined
          ? data.headOfCommitteeName
          : values.headOfCommitteeName;
      const itemsFlat = data.items !== undefined ? data.items : values.items || [];
      const totalFlat = data.total !== undefined ? data.total : values.total;

      // Build nested items matching FixedAssetInventoryItem
      const buildItems = (itemsList) =>
        itemsList.map((item) => ({
          orderNumber: item.orderNumber || "",
          assetName: item.itemName || "",
          unitOfMeasurement: item.unit || "",
          assetCode: item.code || "",
          locationOfUse: item.location || "",
          accountingBook: {
            quantity: num(item.bookQuantity),
            originalCost: num(item.bookCost),
            remainingValue: num(item.bookRemainingValue),
          },
          actualInventory: {
            quantity: num(item.inventoryQuantity),
            originalCost: num(item.inventoryCost),
            remainingValue: num(item.inventoryRemainingValue),
          },
          variance: {
            quantity: num(item.diffQuantity),
            originalCost: num(item.diffCost),
            remainingValue: num(item.diffRemainingValue),
          },
          notes: item.note || "",
        }));

      // Build nested totals
      const totals = {
        accountingBookTotal: {
          quantity: 0,
          originalCost: num(totalFlat.bookCost),
          remainingValue: num(totalFlat.bookRemainingValue),
        },
        actualInventoryTotal: {
          quantity: 0,
          originalCost: num(totalFlat.inventoryCost),
          remainingValue: num(totalFlat.inventoryRemainingValue),
        },
        varianceTotal: {
          quantity: 0,
          originalCost: num(totalFlat.diffCost),
          remainingValue: num(totalFlat.diffRemainingValue),
        },
      };

      // Build inventoryDate ISO string
      const inventoryDate =
        invYear && invMonth && invDay
          ? `${invYear}-${String(invMonth).padStart(2, "0")}-${String(invDay).padStart(2, "0")}`
          : new Date().toISOString().split("T")[0];

      const payload = {
        inventoryDate,
        inventoryCode:
          inventoryCode ||
          `KK/${new Date().getFullYear()}/${String(new Date().getMonth() + 1).padStart(2, "0")}`,
        inventoryCommittee,
        items: buildItems(itemsFlat),
        totals,
        notes: "", // No UI field, optional
        // Legacy fields (if backend still uses)
        unit,
        department,
        invHour,
        signingDate: signingDate || new Date().toISOString(),
        directorName: directorName || "Giám đốc",
        accountantName: accountantName || "Kế toán trưởng",
        headOfCommitteeName: headOfCommitteeName || "Trưởng ban kiểm kê",
      };

      console.log("Submit payload:", JSON.stringify(payload, null, 2));

      if (!id) {
        await FixedAssetsInventorySummaryService.create(payload);
        toast.success("Tạo biên bản kiểm kê TSCĐ thành công!");
      } else {
        await FixedAssetsInventorySummaryService.update(id, payload);
        toast.success("Cập nhật biên bản kiểm kê TSCĐ thành công!");
      }
    } catch (error) {
      console.error("Lỗi khi gọi API lưu:", error);
      toast.error(error?.response?.data?.message || "Lưu thất bại!");
      throw error;
    }
  };

  const handlePrintDirect = (values) => {
    const today = new Date();
    const finalData = { ...values };
    if (data.items) finalData.items = data.items;
    if (data.total) finalData.total = data.total;
    if (data.committee) finalData.committee = data.committee;

    const headerInfo = {
      unit: finalData.unit,
      department: finalData.department,
      docNumber: finalData.documentNumber,
      invHour: finalData.invHour,
      invDay: finalData.invDay,
      invMonth: finalData.invMonth,
      invYear: finalData.invYear,
      committee: finalData.committee,
      signingDate: finalData.signingDate,
    };

    const rows = finalData.items.map((item) => ({
      stt: item.orderNumber,
      tenTscd: item.itemName,
      dvt: item.unit,
      maSo: item.code,
      noiSuDung: item.location,
      bookQuantity: item.bookQuantity,
      bookCost: item.bookCost,
      bookRemValue: item.bookRemainingValue,
      invQuantity: item.inventoryQuantity,
      invCost: item.inventoryCost,
      invRemValue: item.inventoryRemainingValue,
      diffQuantity: item.diffQuantity,
      diffCost: item.diffCost,
      diffRemValue: item.diffRemainingValue,
      note: item.note,
    }));

    const totals = finalData.total || {};

    const htmlContent = getPrintTemplate({
      headerInfo,
      rows,
      totals,
    });

    const printWindow = window.open("", "_blank");
    printWindow.document.open();
    printWindow.document.write(htmlContent);
    printWindow.document.close();
  };

  const handleSubmit = async (values, actions) => {
    try {
      await submitForm(values);
    } catch (error) {
      // Error handled in submitForm
    } finally {
      if (actions) {
        actions.setTouched({});
        actions.setSubmitting(false);
      }
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox py={3} mb={20}>
        <Grid container justifyContent="center" sx={{ height: "100%" }}>
          <Grid item xs={12} lg={12}>
            <Formik initialValues={formInitials} enableReinitialize={true} onSubmit={handleSubmit}>
              {({ values, errors, touched, isSubmitting, setFieldValue }) => (
                <Form autoComplete="off">
                  <Card sx={{ height: "100%" }}>
                    <SoftBox p={2}>
                      <SoftBox>
                        <InventorySummaryTable
                          formData={{
                            values,
                            touched,
                            errors,
                            getData,
                            onChangeValue,
                            setFieldValue,
                          }}
                        />
                        <SoftBox mt={10} width="100%" display="flex" justifyContent="space-between">
                          <SoftButton variant="gradient" color="light" onClick={handleBack}>
                            Trở về
                          </SoftButton>

                          {containView ? (
                            <SoftBox display="flex" gap={3}>
                              <SoftButton
                                variant="gradient"
                                color="success"
                                onClick={() => handlePrintDirect(values)}
                                type="button"
                              >
                                In Biên Bản Kiểm Kê
                              </SoftButton>
                            </SoftBox>
                          ) : (
                            <SoftBox display="flex" gap={3}>
                              <SoftButton
                                disabled={isSubmitting}
                                type="submit"
                                variant="gradient"
                                color="dark"
                              >
                                {id ? "Lưu" : "Tạo"}
                              </SoftButton>
                            </SoftBox>
                          )}
                        </SoftBox>
                      </SoftBox>
                    </SoftBox>
                  </Card>
                </Form>
              )}
            </Formik>
          </Grid>
        </Grid>
      </SoftBox>
    </DashboardLayout>
  );
}

export default FixedAssetsInventorySummaryDetail;
